import React, { useState, useCallback, useMemo, useEffect, forwardRef } from 'react';
import { useParams } from 'react-router-dom';
import { not } from 'ramda';
import PropTypes from 'prop-types';

import {
  usePlanningActions,
  useAlerts,
  ID,
  FILE_ID,
  FILE_URL,
  FILE_URL_EXPIRES,
  BUCKET,
  MIMETYPE,
  NAME,
  ORIGINAL_NAME,
  WARD,
} from 'store/planning';
import { parseDate } from 'utils';
import Close from 'assets/svg-icons/SmallClose';
import Warn from 'components/WarnDeleting';
import { getOriginName } from '../utils';

import { useDragAndDrop } from './hooks';
import Preview from './Preview';
import { Container, ButtonWrap, DeleteBtn, Label } from './styles';

const Item = forwardRef(({ id, fileData, form, setImagePath, saveMedia, removeMedia }, containerRef) => {
  const { id: scheduleId } = useParams();
  const [isShowWarn, setIsShowWarn] = useState();
  const label = useMemo(() => {
    const { lastname, birthday } = form?.patient || {};

    if (!(lastname && birthday)) return getOriginName(fileData[NAME] || fileData[ORIGINAL_NAME]);

    return [lastname, parseDate(birthday)].filter(Boolean).join(' ');
  }, [fileData, form?.patient]);

  const { removeScheduleFormMedia } = usePlanningActions();
  const { action, loading, success, resetAlerts } = useAlerts(removeScheduleFormMedia);
  const handleShowWarn = useCallback(() => setIsShowWarn(not), []);
  const handleDelete = useCallback(() => {
    if (id) action(id);
  }, [action, id]);
  const onDragEnd = useCallback(
    (val) => saveMedia({ scheduleId, id, form: { ...form, ward: val } }),
    [saveMedia, form, id, scheduleId]
  );
  const { onMouseDown, parentRef, dragBoxRef, containerStyle, dragBoxStyle } = useDragAndDrop(containerRef, onDragEnd);

  useEffect(() => {
    if (success) {
      removeMedia(success[ID]);
      resetAlerts();
    }
  }, [fileData, removeMedia, resetAlerts, success]);

  return (
    <Container key={fileData[FILE_ID]} onMouseDown={onMouseDown} style={containerStyle}>
      <Preview
        data={fileData}
        setImageUrl={setImagePath}
        parentRef={parentRef}
        dragBoxRef={dragBoxRef}
        dragBoxStyle={dragBoxStyle}
      />
      {label && <Label>{label}</Label>}
      <ButtonWrap>
        <DeleteBtn type="button" onClick={handleShowWarn}>
          <Close />
        </DeleteBtn>
      </ButtonWrap>
      {isShowWarn && (
        <Warn
          message="Wollen Sie die Datei wirklich löschen?"
          showActon={handleShowWarn}
          deleteAction={handleDelete}
          loading={loading}
          smallWidth
        />
      )}
    </Container>
  );
});

Item.defaultProps = { form: {} };
Item.propTypes = {
  id: PropTypes.string.isRequired,
  fileData: PropTypes.shape({
    [FILE_ID]: PropTypes.string.isRequired,
    [FILE_URL]: PropTypes.string,
    [FILE_URL_EXPIRES]: PropTypes.string,
    [BUCKET]: PropTypes.string.isRequired,
    [MIMETYPE]: PropTypes.string.isRequired,
    [NAME]: PropTypes.string.isRequired,
    [ORIGINAL_NAME]: PropTypes.string.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    patient: PropTypes.shape({
      lastname: PropTypes.string,
      birthday: PropTypes.string,
    }).isRequired,
    [WARD]: PropTypes.string,
  }),
  setImagePath: PropTypes.func.isRequired,
  saveMedia: PropTypes.func.isRequired,
  removeMedia: PropTypes.func.isRequired,
};

export default Item;
